
const DATA_KEY = "localdata";
const CURRENT_TASKS_KEY = "currentTasks";

/* Data type for Units :
* [
*    {
*       "unitId": int,
*       "name": string,
*       "tasksOrder": string, // "id1 id2 id3"
*       "tasks": [
*          {
*            "taskId": int,
*            "name": string,
*            "code": string
*          }
*       ],
*       "issues": [
*          {
*            "issueId": int,
*            "name": string,
*            "color": string,
*            "isActive": boolean,
*            "unitId": int
*          }
*       ],
*       "coachings": [
*          {
*            "coachingId": int,
*            "name": string,
*            "color": string,
*            "isActive": boolean,
*            "unitId": int
*          }
*       ]
*    }
* ]
*/

function getData() {
    const data = localStorage.getItem(DATA_KEY);
    if (data) {
        try {
            return JSON.parse(data);
        } catch (e) {
            console.error("Error parsing data from localStorage:", e);
            return [];
        }
    }
    return [];
}

function saveData(data) {
    localStorage.setItem(DATA_KEY, JSON.stringify(data));
}


/**
 * Get all units from local storage.
 * 
 * @returns {Array} Array of units (unitId, name)
 */
export function getUnits() {
    const data = getData();
    return data.map(unit => ({
        unitId: unit.unitId,
        name: unit.name
    }));
}

/**
 * Save units to local storage.
 * 
 * @param {Array} units Array of units to save (unitId, name)
 */
export function saveUnits(units) {
    const data = getData();
    // Rename existing units with the same id
    const newData = data.map(unit => {
        const foundUnit = units.find(u => u.unitId === unit.unitId);
        if (foundUnit) {
            return { ...unit, name: foundUnit.name };
        }
        return unit;
    });
    // Add new units that do not exist in the current data
    units.filter(u => !data.some(d => d.unitId === u.unitId)).forEach(u => {
        newData.push({ unitId: u.unitId, name: u.name, tasks: [], tasksOrder: "", issues: [], coachings: [] });
    });
    // Remove units that are not in the new list
    const unitIds = new Set(units.map(u => u.unitId));
    newData.forEach((unit, index) => {
        if (!unitIds.has(unit.unitId)) {
            newData.splice(index, 1);
        }
    });
    saveData(newData);
}

/**
 * Get tasks for a specific unit from local storage.
 * 
 * @param {number} unitId The ID of the unit.
 * @returns {Array} Array of tasks for the unit (taskId, name, code)
 */
export function getUnitTasks(unitId) {
    const data = getData();
    return data.find(u => u.unitId === unitId)?.tasks || [];
}

/**
 * Save tasks for a specific unit to local storage.
 * 
 * @param {number} unitId The ID of the unit.
 * @param {Array} tasks Array of tasks to save (taskId, name, code)
 */
export function saveUnitTasks(unitId, tasks) {
    const data = getData();
    const unitIndex = data.findIndex(u => u.unitId === unitId);
    if (unitIndex !== -1) {
        const unit = data[unitIndex];
        unit.tasks = tasks;
        saveData(data);
    }
}

/**
 * Get issues for a specific unit from local storage.
 * 
 * @param {number} unitId The ID of the unit.
 * @returns {Array} Array of issues for the unit (issueId, name, color, isActive)
 */
export function getUnitIssues(unitId) {
    const data = getData();
    return data.find(u => u.unitId === unitId)?.issues || [];
}

/**
 * Save issues for a specific unit to local storage.
 * 
 * @param {number} unitId The ID of the unit.
 * @param {Array} issues Array of issues to save (issueId, name, color, isActive)
 */
export function saveUnitIssues(unitId, issues) {
    const data = getData();
    const unitIndex = data.findIndex(u => u.unitId === unitId);
    if (unitIndex !== -1) {
        const unit = data[unitIndex];
        unit.issues = issues;
        saveData(data);
    }
}

/**
 * Get coachings for a specific unit from local storage.
 * 
 * @param {number} unitId The ID of the unit.
 * @returns {Array} Array of coachings for the unit (coachingId, name, color, isActive)
 */
export function getUnitCoachings(unitId) {
    const data = getData();
    return data.find(u => u.unitId === unitId)?.coachings || [];
}

/**
 * Save coachings for a specific unit to local storage.
 * 
 * @param {number} unitId The ID of the unit.
 * @param {Array} coachings Array of coachings to save (coachingId, name, color, isActive)
 */
export function saveUnitCoachings(unitId, coachings) {
    const data = getData();
    const unitIndex = data.findIndex(u => u.unitId === unitId);
    if (unitIndex !== -1) {
        const unit = data[unitIndex];
        unit.coachings = coachings;
        saveData(data);
    }
}

/**
 * Get task order for a specific unit from local storage.
 * 
 * @param {number} unitId The ID of the unit.
 * @returns {string} Task order string (e.g., "id1 id2 id3")
 */
export function getUnitTasksOrder(unitId) {
    const data = getData();
    return data.find(u => u.unitId === unitId)?.tasksOrder || "";
}

/**
 * Save task order for a specific unit to local storage.
 * 
 * @param {number} unitId The ID of the unit.
 * @param {string} order Task order string (e.g., "id1 id2 id3")
 */
export function saveUnitTasksOrder(unitId, order) {
    const data = getData();
    const unitIndex = data.findIndex(u => u.unitId === unitId);
    if (unitIndex !== -1) {
        const unit = data[unitIndex];
        unit.tasksOrder = order;
        saveData(data);
    }
}

/*
 * Current Tasks type :
 * {
 *    "currentStartTime": string,
 *    "runningStartTime": string,
 *    "runningtimer": boolean,
 *    "tasks": [
 *       {
 *          "color": string,
 *          "taskendtime": string,
 *          "taskname": string,
 *          "taskreference": int,
 *          "tasktime": int,
 *       }
 *    ]
 * }
 */

function getLocalCurrentTasks() {
    const data = localStorage.getItem(CURRENT_TASKS_KEY);
    if (data) {
        try {
            return JSON.parse(data);
        } catch (e) {
            console.error("Error parsing current tasks from localStorage:", e);
            return {};
        }
    }
    return {};
}

function saveLocalCurrentTasks(tasks) {
    localStorage.setItem(CURRENT_TASKS_KEY, JSON.stringify(tasks));
}

/**
 * Get current tasks from local storage.
 * 
 * @returns {Object} Current tasks object (currenttime, runningtime, tasks)
 */
export function getCurrentTasks() {
    const now = new Date();
    const data = getLocalCurrentTasks();
    return {
        currenttime : data.currentStartTime ? Math.floor((now - new Date(data.currentStartTime)) / 1000) : 0,
        runningtime: data.runningtimer && data.runningStartTime ? Math.floor((now - new Date(data.runningStartTime)) / 1000) : 0,
        tasks: data.tasks || []
    };
}

/**
 * Save current tasks to local storage.
 * To turn off the running timer, set `runningTime` to -1.
 * 
 * @param {Number} currentTime Current time in seconds
 * @param {Number} runningTime Running time in seconds
 * @param {Object} tasks Current tasks object
 */
export function saveCurrentTasks(currentTime, runningTime, tasks) {
    const now = new Date();
    const timeRunning = runningTime === -1 ? 0 : runningTime;
    const newTasks = {
        currentStartTime : new Date(now.getTime() - currentTime * 1000).toISOString(),
        runningStartTime: new Date(now.getTime() - timeRunning * 1000).toISOString(),
        runningtimer: runningTime > -1,
        tasks: tasks || []
    };
    saveLocalCurrentTasks(newTasks);
}
